import { api } from "./api";

export interface Role {
  id: number;
  nombre: string;
  permissions?: string[];
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: Role;
  phone?: string;
  document_type?: string;
  document_number?: string;
  country?: string;
  city?: string;
  department?: string;
  profile_photo_path?: string;
  profile_photo_url?: string;
  cv_path?: string;
  cv_url?: string;
  tarjeta_profesional_path?: string;
  tarjeta_profesional_url?: string;
  especialidad_revisoria_fiscal?: boolean;
  especialidad_auditoria_externa?: boolean;
  especialidad_evaluacion_estructuras?: boolean;
  especialidad_valoracion_empresas?: boolean;
  especialidad_control_interno?: boolean;
  especialidad_auditoria_financiera?: boolean;
  especialidad_analisis_riesgos?: boolean;
  especialidad_otros?: boolean;
}

export interface UserAdjunto {
  id: number;
  user_id: number;
  nombre: string;
  original_name: string;
  mime_type?: string;
  size?: number;
  url: string | null;
  created_at: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface UpdateProfileData {
  name: string;
  email: string;
  phone?: string;
  document_type?: string;
  document_number?: string;
  country?: string;
  city?: string;
  department?: string;
  especialidad_revisoria_fiscal?: boolean;
  especialidad_auditoria_externa?: boolean;
  especialidad_evaluacion_estructuras?: boolean;
  especialidad_valoracion_empresas?: boolean;
  especialidad_control_interno?: boolean;
  especialidad_auditoria_financiera?: boolean;
  especialidad_analisis_riesgos?: boolean;
  especialidad_otros?: boolean;
}

export interface UpdatePasswordData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

class AuthService {
  private tokenKey = "auth_token";

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/login", credentials);
    this.setToken(response.access_token);
    return response;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/register", data);
    this.setToken(response.access_token);
    return response;
  }

  async logout(): Promise<void> {
    try {
      await api.post("/logout", {});
    } finally {
      this.removeToken();
    }
  }

  async getCurrentUser(): Promise<User> {
    return api.get<User>("/me");
  }

  async updateProfile(
    data: UpdateProfileData,
  ): Promise<{ message: string; user: User }> {
    return api.put<{ message: string; user: User }>("/profile", data);
  }

  async updatePhoto(
    file: File,
  ): Promise<{ message: string; user: User; photo_url: string }> {
    const formData = new FormData();
    formData.append("photo", file);
    return api.post<{ message: string; user: User; photo_url: string }>(
      "/profile/photo",
      formData,
    );
  }

  async updatePassword(data: UpdatePasswordData): Promise<{ message: string }> {
    return api.put<{ message: string }>("/password", data);
  }

  async uploadCV(
    file: File,
  ): Promise<{ message: string; user: User; cv_url: string }> {
    const formData = new FormData();
    formData.append("cv", file);
    return api.post<{ message: string; user: User; cv_url: string }>(
      "/profile/cv",
      formData,
    );
  }

  async uploadTarjetaProfesional(
    file: File,
  ): Promise<{ message: string; user: User; tarjeta_profesional_url: string }> {
    const formData = new FormData();
    formData.append("tarjeta_profesional", file);
    return api.post<{ message: string; user: User; tarjeta_profesional_url: string }>(
      "/profile/tarjeta-profesional",
      formData,
    );
  }

  async getAdjuntos(): Promise<UserAdjunto[]> {
    return api.get<UserAdjunto[]>("/profile/adjuntos");
  }

  async uploadAdjunto(
    nombre: string,
    file: File,
  ): Promise<{ message: string; adjunto: UserAdjunto }> {
    const formData = new FormData();
    formData.append("nombre", nombre);
    formData.append("archivo", file);
    return api.post<{ message: string; adjunto: UserAdjunto }>(
      "/profile/adjuntos",
      formData,
    );
  }

  async deleteAdjunto(id: number): Promise<{ message: string }> {
    return api.delete<{ message: string }>(`/profile/adjuntos/${id}`);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const authService = new AuthService();
